import { z } from 'zod';
import { BusinessDateSchema, IdSchema } from './encoding.js';
import { SensitivityCategorySchema } from './identity.js';
/**
 * Document subsystem contracts (SDD §17; ERD §8.3; FRD-CORE-019 to CORE-024).
 *
 * A document is owned by exactly one record (a record reference, ERD §8.9) and its access follows
 * that record (FRD-CORE-021). The binary content is never in these DTOs: it is uploaded as
 * `multipart/form-data` and downloaded from `GET /api/v1/documents/{id}/content` (SDD §10.8).
 */
/**
 * The content types the upload sniffer can verify from the file itself (SDD §12.5). A Document
 * Type allows a subset of these; the database constrains to the same list.
 */
export const DocumentContentTypeSchema = z.enum([
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    // Legacy Word and Excel (.doc, .xls), added 9 October 2026.
    'application/msword',
    'application/vnd.ms-excel',
    'text/plain',
    'text/csv',
    // Audio, for chat voice messages and recordings, added 16 October 2026 (CLR-62).
    'audio/webm',
    'audio/ogg',
    'audio/mp4',
    'audio/mpeg',
    'audio/wav',
]);
/** SDD §17.1. Only `available` can be downloaded (SDD §17.5). */
export const DocumentStatusSchema = z.enum([
    'pending_scan',
    'available',
    'quarantined',
    'superseded',
    'scan_failed',
]);
const RecordTypeCodeSchema = z
    .string()
    .regex(/^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/, 'must be <module>.<record>')
    .max(100);
const TypeCodeSchema = z
    .string()
    .regex(/^[a-z][a-z0-9_]{0,49}$/, 'lower case letters, digits and underscores, up to 50');
const TypeNameSchema = z.string().trim().min(1).max(100);
const RetentionClassSchema = z.string().trim().min(1).max(50);
// Room for every supported type: a chat attachment type may take documents, images and audio.
const AllowedTypesSchema = z.array(DocumentContentTypeSchema).min(1).max(20);
export const DocumentTypeSchema = z.object({
    id: IdSchema,
    code: z.string(),
    name: z.string(),
    applicableRecordType: z.string(),
    requiredForRecord: z.boolean(),
    expiryTracked: z.boolean(),
    confidentialityCategory: SensitivityCategorySchema.nullable(),
    /** Informational: the retention periods are pending CLR-19, and nothing is deleted in R1. */
    retentionClass: z.string().nullable(),
    replacementAllowed: z.boolean(),
    allowedContentTypes: z.array(DocumentContentTypeSchema),
    status: z.enum(['active', 'inactive']),
    version: z.number().int(),
});
export const CreateDocumentTypeRequestSchema = z.strictObject({
    code: TypeCodeSchema,
    name: TypeNameSchema,
    applicableRecordType: RecordTypeCodeSchema,
    requiredForRecord: z.boolean().optional(),
    expiryTracked: z.boolean().optional(),
    confidentialityCategory: SensitivityCategorySchema.optional(),
    retentionClass: RetentionClassSchema.optional(),
    replacementAllowed: z.boolean().optional(),
    allowedContentTypes: AllowedTypesSchema,
});
/** A partial change. The code and the applicable record type are fixed once created. */
export const UpdateDocumentTypeRequestSchema = z
    .strictObject({
    name: TypeNameSchema.optional(),
    requiredForRecord: z.boolean().optional(),
    expiryTracked: z.boolean().optional(),
    /** `null` removes the confidentiality category. */
    confidentialityCategory: SensitivityCategorySchema.nullable().optional(),
    retentionClass: RetentionClassSchema.nullable().optional(),
    replacementAllowed: z.boolean().optional(),
    allowedContentTypes: AllowedTypesSchema.optional(),
    status: z.enum(['active', 'inactive']).optional(),
})
    .refine((value) => Object.keys(value).length > 0, 'at least one change is required');
/** A record type that can own documents: one with a registered owner policy (SDD §17.6). */
export const DocumentOwnerTypeSchema = z.object({
    code: z.string(),
    displayName: z.string(),
    /** Whether the owning record type declares `view_unmasked`, which a confidential type needs. */
    supportsConfidentiality: z.boolean(),
});
export const DocumentSchema = z.object({
    id: IdSchema,
    ownerRecordType: z.string(),
    ownerRecordId: IdSchema,
    documentTypeId: IdSchema,
    documentTypeCode: z.string(),
    documentTypeName: z.string(),
    expiryTracked: z.boolean(),
    replacementAllowed: z.boolean(),
    /** The type carries a confidentiality category; the caller holds its grant, or would not see it. */
    confidential: z.boolean(),
    title: z.string(),
    originalFileName: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int(),
    sha256: z.string(),
    status: DocumentStatusSchema,
    supersedesDocumentId: IdSchema.nullable(),
    documentNumber: z.string().nullable(),
    issueDate: z.string().nullable(),
    expiryDate: z.string().nullable(),
    origin: z.enum(['uploaded', 'system_generated']),
    uploadedBy: IdSchema.nullable(),
    uploadedByName: z.string().nullable(),
    uploadedAt: z.string(),
    version: z.number().int(),
});
/** A row of the expiring-documents list, with the owning record named for its link. */
export const ExpiringDocumentSchema = DocumentSchema.extend({
    ownerLabel: z.string().nullable(),
});
/**
 * The text fields of an upload (`multipart/form-data`, sent **before** the `file` part so the
 * API can authorize before it stores anything, SDD §17.4). Empty optional fields are omitted.
 */
export const UploadDocumentFieldsSchema = z.strictObject({
    ownerRecordType: RecordTypeCodeSchema,
    ownerRecordId: IdSchema,
    documentTypeCode: TypeCodeSchema,
    title: z.string().trim().min(1).max(200),
    documentNumber: z.string().trim().min(1).max(100).optional(),
    issueDate: BusinessDateSchema.optional(),
    expiryDate: BusinessDateSchema.optional(),
});
/** A replacement names no owner: it belongs to the replaced document's record (SDD §17.3). */
export const ReplaceDocumentFieldsSchema = UploadDocumentFieldsSchema.omit({
    ownerRecordType: true,
    ownerRecordId: true,
}).extend({ documentTypeCode: TypeCodeSchema.optional() });
/**
 * The details of a current document, corrected without a new file (CLR-45): the whole set is sent,
 * and `null` clears an optional detail. The file, type and owning record never change here.
 */
export const UpdateDocumentDetailsRequestSchema = z.strictObject({
    title: z.string().trim().min(1).max(200),
    documentNumber: z.string().trim().min(1).max(100).nullable(),
    issueDate: BusinessDateSchema.nullable(),
    expiryDate: BusinessDateSchema.nullable(),
});
export const OwnerDocumentsQuerySchema = z.strictObject({
    ownerRecordType: RecordTypeCodeSchema,
    ownerRecordId: IdSchema,
});
export const DocumentTypesQuerySchema = z.strictObject({
    recordType: RecordTypeCodeSchema,
    /**
     * The record an upload is for: confidential types are then offered only when the caller holds
     * their category grant on that record (the same check the upload makes, SDD §17.6).
     */
    ownerRecordId: z
        .string()
        .regex(/^[1-9]\d{0,18}$/)
        .optional(),
});
export const ExpiringDocumentsQuerySchema = z.strictObject({
    withinDays: z.coerce.number().int().min(0).max(3650),
    recordType: RecordTypeCodeSchema.optional(),
    page: z.coerce.number().int().min(1).max(100_000).optional(),
    pageSize: z.coerce.number().int().min(1).max(200).optional(),
});
/**
 * A byte count as people read it — "5 MB", "1.5 MB", "800 KB" — in the binary units the upload
 * limit is set in (1 MB = 1,048,576 bytes), so the limit itself always reads as a whole number.
 */
export function fileSizeLabel(bytes) {
    const mb = bytes / (1024 * 1024);
    if (mb >= 1)
        return `${String(Number(mb.toFixed(mb >= 10 ? 0 : 1)))} MB`;
    return `${String(Math.max(1, Math.round(bytes / 1024)))} KB`;
}
//# sourceMappingURL=documents.js.map